import { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  AddNoteRequestCatalogueType,
  DeleteNoteRequestCatalogueType,
  type StaffGetNotesQueryKey,
  type StaffNoteModel,
  StaffNoteModelCatalogueType,
  type StaffNoteModelColour,
  SetNoteColourRequestCatalogueType,
  SetNoteColourRequestColour,
  ToggleNotePinRequestCatalogueType,
  useInvalidateUseStaffGetNotes,
  useStaffAddNote,
  useStaffDeleteNote,
  useStaffGetNotes,
  useStaffSetNoteColour,
  useStaffToggleNotePin,
} from '@urbanx/agx.staff.client';
import { AgxNotes, type AgxNote, useToast } from '@urbanx/agx.ui-components2';
import { getErrorMessage } from 'helpers/getErrorMessage';

const CATALOGUE_TYPE = StaffNoteModelCatalogueType.Campaign;
const MAX_NOTE_LENGTH = 1000;

const toggleNotePinInOrder = (notes: StaffNoteModel[], noteId: string) => {
  const note = notes.find((candidate) => candidate.noteId === noteId)!;
  const toggledNote = { ...note, isPinned: !note.isPinned };
  const remainingNotes = notes.filter((candidate) => candidate.noteId !== noteId);

  if (toggledNote.isPinned) return [toggledNote, ...remainingNotes];

  const pinnedNotes = remainingNotes.filter((candidate) => candidate.isPinned);
  const unpinnedNotes = [
    ...remainingNotes.filter((candidate) => !candidate.isPinned),
    toggledNote,
  ].sort(
    (left, right) =>
      new Date(String(right.createdAt)).getTime() - new Date(String(left.createdAt)).getTime()
  );

  return [...pinnedNotes, ...unpinnedNotes];
};

type CampaignStickyNotesProps = {
  campaignId: string;
};

const CampaignStickyNotes = ({ campaignId }: CampaignStickyNotesProps) => {
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const invalidateNotes = useInvalidateUseStaffGetNotes();
  const queryKey: StaffGetNotesQueryKey = ['staffGetNotes', CATALOGUE_TYPE, campaignId];
  const { data, isPending, isError } = useStaffGetNotes(CATALOGUE_TYPE, campaignId);

  const refreshNotes = () => invalidateNotes(CATALOGUE_TYPE, campaignId);
  const mutationSucceeded = () => {
    void refreshNotes();
  };
  const showMutationError = (error: unknown, fallback: string) => {
    pushToast({
      type: 'error',
      message: getErrorMessage(error, fallback),
    });
  };

  const { staffAddNote } = useStaffAddNote({
    onSuccess: mutationSucceeded,
    onError: (error) => showMutationError(error, 'Unable to add note'),
  });
  const { staffDeleteNote, staffDeleteNotePending } = useStaffDeleteNote({
    onSuccess: mutationSucceeded,
    onError: (error) => showMutationError(error, 'Unable to delete note'),
  });
  const { staffToggleNotePin, staffToggleNotePinPending } = useStaffToggleNotePin({
    onSuccess: mutationSucceeded,
    onError: (error) => showMutationError(error, 'Unable to update note pin'),
  });
  const { staffSetNoteColour, staffSetNoteColourPending } = useStaffSetNoteColour({
    onSuccess: mutationSucceeded,
    onError: (error) => showMutationError(error, 'Unable to update note colour'),
  });

  const notes: AgxNote[] = [];
  for (const note of data ?? []) {
    const { noteId, text, createdAt, colour } = note;
    if (!noteId || text === undefined || !createdAt || !colour) continue;

    notes.push({ ...note, noteId, text, createdAt: new Date(String(createdAt)), colour });
  }

  const addNote = async (text: string) => {
    try {
      await staffAddNote({
        catalogueType: AddNoteRequestCatalogueType.Campaign,
        catalogueId: campaignId,
        text: text.slice(0, MAX_NOTE_LENGTH),
      });
      return true;
    } catch {
      return false;
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      await staffDeleteNote({
        catalogueType: DeleteNoteRequestCatalogueType.Campaign,
        catalogueId: campaignId,
        noteId,
      });
      return true;
    } catch {
      return false;
    }
  };

  const togglePin = async (noteId: string) => {
    const previousNotes = queryClient.getQueryData<StaffNoteModel[]>(queryKey);
    queryClient.setQueryData<StaffNoteModel[]>(queryKey, (current) =>
      current ? toggleNotePinInOrder(current, noteId) : current
    );

    try {
      await staffToggleNotePin({
        catalogueType: ToggleNotePinRequestCatalogueType.Campaign,
        catalogueId: campaignId,
        noteId,
      });
    } catch {
      queryClient.setQueryData(queryKey, previousNotes);
      await refreshNotes();
    }
  };

  const setColour = async (noteId: string, colour: StaffNoteModelColour) => {
    const previousNotes = queryClient.getQueryData<StaffNoteModel[]>(queryKey);
    queryClient.setQueryData<StaffNoteModel[]>(queryKey, (current) =>
      current?.map((note) => (note.noteId === noteId ? { ...note, colour } : note))
    );

    try {
      await staffSetNoteColour({
        catalogueType: SetNoteColourRequestCatalogueType.Campaign,
        catalogueId: campaignId,
        noteId,
        colour: SetNoteColourRequestColour[colour as keyof typeof SetNoteColourRequestColour],
      });
    } catch {
      queryClient.setQueryData(queryKey, previousNotes);
      await refreshNotes();
    }
  };

  return (
    <AgxNotes
      id={`campaign-${campaignId}`}
      layoutPersistenceKey={`campaign:${campaignId}`}
      notes={notes}
      currentUserId={auth?.userAccount?.agent ?? ''}
      isPending={isPending}
      isError={isError}
      isDeletingNote={staffDeleteNotePending}
      isTogglingPin={staffToggleNotePinPending}
      isSettingColour={staffSetNoteColourPending}
      maxNoteLength={MAX_NOTE_LENGTH}
      onAddNote={addNote}
      onDeleteNote={deleteNote}
      onTogglePin={(noteId) => void togglePin(noteId)}
      onSetColour={(noteId, colour) => void setColour(noteId, colour)}
    />
  );
};

export default CampaignStickyNotes;
